import * as React from "react";
import { useNavigate } from "react-router";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";
import { useViewport } from "@/lib/useViewport";
import { useUnreadCount } from "@/api/notifications";
import { NotificationsPopover } from "./NotificationsPopover";
import { useAlarmNotices } from "./useAlarmNotices";

/**
 * Header bell + unread badge (spec §2.1 + §4.3).
 *
 * Desktop / tablet (≥ 768 px): clicking the bell opens the popover.
 * Mobile (< 768 px): clicking the bell navigates straight to
 * ``/notifications`` — no popover (the popover doesn't fit in a
 * 375 px viewport without overlapping the rest of the chrome).
 *
 * Badge:
 *   * 16 px wide on 1-2 digits, 20 px on 3, "99+" cap (spec §7.1)
 *   * Pulses for 1.5 s on a count increase from a non-zero source
 *     (avoids the page-load animation flash).
 *   * Reduced-motion = no pulse, just colour swap.
 *
 * MV2-1240: the badge is red only while an unread critical or major alarm
 * is waiting (the brand colour for everything else), and new urgent alarms
 * toast while the page is open (``useAlarmNotices``).
 *
 * Keyboard: ``n`` toggles the popover from anywhere (spec §4.1).
 */
export const NotificationsBell: React.FC = () => {
  const { isMobile } = useViewport();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const unread = useUnreadCount();
  const count = unread.data?.unread ?? 0;
  const { urgent } = useAlarmNotices();
  const lastCountRef = React.useRef<number>(count);
  const [pulse, setPulse] = React.useState(false);

  // Pulse the badge briefly when the count climbs from a non-zero
  // baseline. Skipping the initial 0 → N transition avoids a fake
  // "new!" pulse on every cold reload.
  React.useEffect(() => {
    const prev = lastCountRef.current;
    if (count > prev && prev > 0) {
      setPulse(true);
      const t = window.setTimeout(() => setPulse(false), 1500);
      return () => window.clearTimeout(t);
    }
    lastCountRef.current = count;
    return undefined;
  }, [count]);

  // ``n`` keyboard toggle — only fires when no input/textarea has
  // focus, so users typing "no" in a comment don't pop the bell.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      if (e.key !== "n") return;
      const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      const editable = (e.target as HTMLElement | null)?.isContentEditable;
      if (editable) return;
      e.preventDefault();
      if (isMobile) {
        navigate("/notifications");
      } else {
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isMobile, navigate]);

  const badgeText = count > 99 ? "99+" : String(count);
  const urgentText = urgent > 0 ? ` · ${urgent} urgent ${urgent === 1 ? "alarm" : "alarms"}` : "";
  const ariaLabel = count > 0
    ? `Notifications, ${count} unread${urgentText}`
    : "Notifications";

  // Same chrome on both mobile + desktop. Mobile renders a plain
  // button that navigates to /notifications; desktop renders the
  // same button as the Radix Popover.Trigger child. Keeping the
  // ``onClick`` off the Button on desktop is deliberate — defining
  // even a no-op handler caused Radix's Slot composition to skip
  // its own toggle handler in some environments (Vite HMR + custom
  // ButtonElement). The desktop trigger lets Radix attach its own
  // onClick exclusively.
  const bellInner = (
    <>
      <Bell size={18} />
      {count > 0 ? (
        <span
          className={cn(
            "absolute right-0 top-0 inline-flex items-center justify-center",
            "rounded-pill px-1 text-[10px] font-semibold leading-none",
            urgent > 0
              ? "bg-[color:var(--danger)] text-gray-0"
              : "bg-[color:var(--brand-primary)] text-[color:var(--brand-primary-fg)]",
            "tabular pointer-events-none",
            count > 99 ? "h-4 min-w-[20px]" : "h-4 min-w-[16px]",
            pulse && "motion-safe:animate-pulse",
          )}
          aria-hidden="true"
        >
          {badgeText}
        </span>
      ) : null}
    </>
  );

  if (isMobile) {
    return (
      <Button
        variant="ghost"
        size="icon"
        aria-label={ariaLabel}
        title={ariaLabel}
        onClick={() => navigate("/notifications")}
        className="relative"
      >
        {bellInner}
      </Button>
    );
  }

  return (
    <NotificationsPopover
      open={open}
      onOpenChange={setOpen}
      trigger={
        <Button
          variant="ghost"
          size="icon"
          aria-label={ariaLabel}
          title={ariaLabel}
          className="relative"
        >
          {bellInner}
        </Button>
      }
    />
  );
};
